import AIAPIService from './AIAPIService.js'

class OpenAITestService {
  constructor() {
    this.apiService = new AIAPIService()
  }

  async runAllTests() {
    console.log('🧪 Starting API Tests...')
    
    const results = {
      apiKeyCheck: await this.testAPIKey(),
      contentGeneration: await this.testContentGeneration(),
      imageGeneration: await this.testImageGeneration(),
      errorHandling: await this.testErrorHandling()
    }

    this.displayResults(results)
    return results
  }

  async testAPIKey() {
    console.log('🔑 Testing API Key...')
    
    const hasKey = !!this.apiService.apiKeys.openai
    const keyLength = this.apiService.apiKeys.openai?.length || 0
    const validFormat = this.apiService.apiKeys.openai?.startsWith('sk-') || false
    
    // Test basic API connectivity
    let apiTest = false
    if (hasKey && validFormat) {
      try {
        const response = await fetch('https://api.openai.com/v1/models', {
          headers: {
            'Authorization': `Bearer ${this.apiService.apiKeys.openai}`
          }
        })
        apiTest = response.ok
        console.log('API connectivity test:', apiTest ? '✅ Success' : '❌ Failed')
      } catch (error) {
        console.log('API connectivity test failed:', error.message)
      }
    }
    
    return {
      success: hasKey && keyLength > 50 && validFormat && apiTest,
      details: {
        hasKey,
        keyLength,
        validFormat,
        apiTest,
        keyPrefix: this.apiService.apiKeys.openai?.substring(0, 10) + '...' || 'None'
      }
    }
  }

  async testContentGeneration() {
    console.log('📝 Testing Content Generation...')
    
    try {
      const result = await this.apiService.generateContent(
        'Create a simple welcome message for a digital signage display',
        'text',
        { maxTokens: 100, temperature: 0.7 }
      )

      return {
        success: result.success,
        content: result.content,
        error: result.error,
        usage: result.usage
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  async testImageGeneration() {
    console.log('🎨 Testing AI Image Generation...')
    
    try {
      const imageResult = await this.apiService.generateImage(
        "modern office workspace",
        { size: "1024x1024", style: "natural" }
      )

      return {
        success: imageResult.success,
        imageUrl: imageResult.images?.[0],
        error: imageResult.error,
        usage: imageResult.usage,
        source: imageResult.source
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  async testErrorHandling() {
    console.log('⚠️ Testing Error Handling...')
    
    const tests = []
    
    // Test 1: Empty prompt
    try {
      const result1 = await this.apiService.generateContent('', 'text')
      tests.push({
        test: 'Empty prompt',
        success: !result1.success && !!result1.error,
        error: result1.error
      })
    } catch (error) {
      tests.push({
        test: 'Empty prompt',
        success: true,
        error: error.message
      })
    }
    
    // Test 2: Invalid image prompt
    try {
      const result2 = await this.apiService.generateImage('', {})
      tests.push({
        test: 'Empty image prompt',
        success: !result2.success && !!result2.error,
        error: result2.error
      })
    } catch (error) {
      tests.push({
        test: 'Empty image prompt',
        success: true,
        error: error.message
      })
    }
    
    // Test 3: Very long prompt (should be handled gracefully)
    try {
      const longPrompt = 'A'.repeat(2000) // Exceeds AI service limit
      const result3 = await this.apiService.generateImage(longPrompt, {})
      tests.push({
        test: 'Long prompt handling',
        success: result3.success || !!result3.error,
        error: result3.error
      })
    } catch (error) {
      tests.push({
        test: 'Long prompt handling',
        success: true,
        error: error.message
      })
    }
    
    const allTestsPassed = tests.every(test => test.success)
    
    return {
      success: allTestsPassed,
      tests: tests,
      errorHandled: allTestsPassed,
      errorMessage: allTestsPassed ? 'All error scenarios handled correctly' : 'Some error scenarios failed'
    }
  }

  displayResults(results) {
    console.log('\n📊 OpenAI API Test Results:')
    console.log('=' .repeat(50))
    
    // API Key Test
    console.log('\n🔑 API Key Test:')
    if (results.apiKeyCheck.success) {
      console.log('✅ API Key is configured correctly')
      console.log(`   Key length: ${results.apiKeyCheck.details.keyLength} characters`)
      console.log(`   Key prefix: ${results.apiKeyCheck.details.keyPrefix}`)
      console.log(`   Valid format: ${results.apiKeyCheck.details.validFormat ? '✅' : '❌'}`)
      console.log(`   API connectivity: ${results.apiKeyCheck.details.apiTest ? '✅' : '❌'}`)
    } else {
      console.log('❌ API Key is missing or invalid')
      console.log(`   Has key: ${results.apiKeyCheck.details.hasKey ? '✅' : '❌'}`)
      console.log(`   Valid format: ${results.apiKeyCheck.details.validFormat ? '✅' : '❌'}`)
      console.log(`   API connectivity: ${results.apiKeyCheck.details.apiTest ? '✅' : '❌'}`)
      console.log('   Please check your .env file')
    }

    // Content Generation Test
    console.log('\n📝 Content Generation Test:')
    if (results.contentGeneration.success) {
      console.log('✅ Content generation working')
      console.log(`   Generated: "${results.contentGeneration.content?.substring(0, 100)}..."`)
      if (results.contentGeneration.usage) {
        console.log(`   Tokens used: ${results.contentGeneration.usage.total_tokens}`)
      }
    } else {
      console.log('❌ Content generation failed')
      console.log(`   Error: ${results.contentGeneration.error}`)
    }

    // Image Generation Test
    console.log('\n🎨 AI Image Generation Test:')
    if (results.imageGeneration.success) {
      console.log('✅ AI image generation working')
      console.log(`   Source: ${results.imageGeneration.source || 'AI Service'}`)
      console.log(`   Image URL: ${results.imageGeneration.imageUrl}`)
      if (results.imageGeneration.usage) {
        console.log(`   Usage: ${JSON.stringify(results.imageGeneration.usage)}`)
      }
    } else {
      console.log('❌ AI image generation failed')
      console.log(`   Error: ${results.imageGeneration.error}`)
    }

    // Error Handling Test
    console.log('\n⚠️ Error Handling Test:')
    if (results.errorHandling.success) {
      console.log('✅ Error handling working correctly')
      console.log(`   ${results.errorHandling.errorMessage}`)
      if (results.errorHandling.tests) {
        results.errorHandling.tests.forEach(test => {
          console.log(`   • ${test.test}: ${test.success ? '✅' : '❌'} ${test.error || 'Handled correctly'}`)
        })
      }
    } else {
      console.log('❌ Error handling failed')
      console.log(`   Error: ${results.errorHandling.errorMessage}`)
      if (results.errorHandling.tests) {
        results.errorHandling.tests.forEach(test => {
          console.log(`   • ${test.test}: ${test.success ? '✅' : '❌'} ${test.error || 'Handled correctly'}`)
        })
      }
    }

    // Overall Status
    const allTestsPassed = Object.values(results).every(test => test.success)
    console.log('\n🎯 Overall Status:')
    if (allTestsPassed) {
      console.log('✅ All tests passed! OpenAI service is working correctly.')
    } else {
      console.log('❌ Some tests failed. Check the details above.')
    }
    
    console.log('=' .repeat(50))
  }

  async quickTest() {
    console.log('⚡ Running Quick OpenAI Test...')
    
    const apiKeyTest = await this.testAPIKey()
    if (!apiKeyTest.success) {
      console.log('❌ API Key not configured. Please check your .env file.')
      return false
    }

    const contentTest = await this.testContentGeneration()
    if (contentTest.success) {
      console.log('✅ OpenAI service is working!')
      console.log(`Sample content: "${contentTest.content?.substring(0, 50)}..."`)
      return true
    } else {
      console.log('❌ OpenAI service test failed:', contentTest.error)
      return false
    }
  }
}

export default OpenAITestService 